import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'

import { makeSelectContentTypesByModel } from '@webapp/core/stores/global'
import { permissionRepository } from '@webapp/platform/iam/data-access/repositories/PermissionRepository'
import {
  type ReportAccessGrant,
  type ReportAccessGrantConstructor,
} from '@webapp/platform/iam/entities/ReportAccessGrant'
import { reportAccessGrantQueryConfig } from '@webapp/platform/iam/query-configs/reportAccessGrant'

export const useMutateRevokeReportAccessGrant = () => {
  const queryClient = useQueryClient()
  const secondaryCategoryContentType = useSelector(
    makeSelectContentTypesByModel('procurify', 'department')
  )

  const buildListQueryKey = (secondaryCategoryId: number) =>
    reportAccessGrantQueryConfig.list({
      secondaryCategoryId,
      secondaryCategoryContentTypeId: secondaryCategoryContentType.id,
    }).queryKey

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: ({
      grant,
    }: {
      grant: ReportAccessGrant
      secondaryCategoryId: number
    }) => permissionRepository.revokeReportAccessGrant(grant.id),
    // Optimistic removal (BHV-017) on the FULL key (ADR-5)
    onMutate: ({ grant, secondaryCategoryId }) => {
      const queryKey = buildListQueryKey(secondaryCategoryId)
      const previousGrants =
        queryClient.getQueryData<ReportAccessGrantConstructor[]>(queryKey)

      queryClient.setQueryData<ReportAccessGrantConstructor[]>(
        queryKey,
        (existing = []) =>
          existing.filter((existingGrant) => existingGrant.id !== grant.id)
      )

      return { previousGrants }
    },
    // Rollback: restore the removed grant to the TOP of the list (BHV-018)
    onError: (_err, { grant, secondaryCategoryId }) => {
      const queryKey = buildListQueryKey(secondaryCategoryId)

      queryClient.setQueryData<ReportAccessGrantConstructor[]>(
        queryKey,
        (existing = []) => [
          {
            id: grant.id,
            userFirstName: grant.userFirstName,
            userLastName: grant.userLastName,
          },
          ...existing.filter((existingGrant) => existingGrant.id !== grant.id),
        ]
      )
    },
  })

  return {
    revokeReportAccessGrant: mutateAsync,
    isRevoking: isLoading,
  }
}
